/**
 * Preferences › Voice › Dictation (desktop): the vocabulary, which serves all
 * voice input, then dictation anywhere. Its switch is the shortcut's own
 * on/off state; the rows under it set how the text arrives and open the
 * shortcut and its prompts. While the switch is off they are inactive.
 */
import { useEffect, useState } from "react";
import { PocketRow, PocketSection, ToggleSwitch } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import { useDictationAnywhereSettingsStore } from "@/stores/dictation-anywhere-settings-store";
import { shortcutNeedsKey, useShortcutStore } from "@/stores/shortcut-store";
import { useSettingsNav } from "./settings-navigation";
import { SettingsFieldError } from "./settings-text-field";
import { DictationShortcutCaps } from "./voice-dictation-key-caps";
import { DICTATION_SHORTCUT_ID, promptLabel } from "./voice-dictation-keys";
import { VoiceVocabularyRow } from "./voice-vocabulary-row";

const INACTIVE = "opacity-50";

export function VoiceDictationSection() {
  const nav = useSettingsNav();
  const t = useT(settingsVoiceMessages);
  const hydrate = useShortcutStore((state) => state.hydrate);
  const disabled = useShortcutStore((state) =>
    state.disabled.includes(DICTATION_SHORTCUT_ID),
  );
  const needsKey = useShortcutStore((state) =>
    shortcutNeedsKey(state, DICTATION_SHORTCUT_ID),
  );
  const setDisabled = useShortcutStore((state) => state.setDisabled);
  const autoPaste = useDictationAnywhereSettingsStore(
    (state) => state.autoPaste,
  );
  const setAutoPaste = useDictationAnywhereSettingsStore(
    (state) => state.setAutoPaste,
  );
  const prompts = useDictationAnywhereSettingsStore((state) => state.prompts);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isTauri()) void hydrate();
  }, [hydrate]);
  if (!isTauri()) return null;

  // Waiting for a key is still on: the user never switched it off.
  const on = !disabled || needsKey;
  const names = [t("promptDefault"), ...prompts.map(promptLabel)].join(
    t("listSeparator"),
  );

  async function switchFeature(next: boolean) {
    setError(null);
    try {
      await setDisabled(DICTATION_SHORTCUT_ID, !next);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }

  return (
    <PocketSection title={t("dictation")} testId="voice-dictation">
      <VoiceVocabularyRow />
      <PocketRow
        testId="settings-row-voice-dictation-anywhere"
        title={t("dictateAnywhere")}
        trailing={
          <ToggleSwitch
            checked={on}
            onCheckedChange={(next) => void switchFeature(next)}
            aria-label={t("dictateAnywhere")}
            data-testid="voice-dictation-anywhere-toggle"
          />
        }
        below={
          <SettingsFieldError
            error={error}
            testId="voice-dictation-anywhere-error"
          />
        }
      />
      <PocketRow
        testId="settings-row-voice-dictation-paste"
        title={t("pasteAutomatically")}
        className={cn(!on && INACTIVE)}
        trailing={
          <ToggleSwitch
            checked={autoPaste}
            onCheckedChange={setAutoPaste}
            disabled={!on}
            className="disabled:opacity-100"
            aria-label={t("pasteAutomatically")}
            data-testid="voice-dictation-paste-toggle"
          />
        }
      />
      <PocketRow
        testId="settings-row-voice-dictation-prompts"
        title={t("shortcutAndPrompts")}
        secondLine={needsKey ? t("keyInUse") : names}
        status={needsKey ? { tone: "question" } : undefined}
        trailing={needsKey ? undefined : <DictationShortcutCaps />}
        chevron
        className={cn(!on && INACTIVE)}
        onSelect={
          on ? () => nav.push({ page: "preferences-voice-prompts" }) : undefined
        }
      />
    </PocketSection>
  );
}
